import { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Avatar,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import { AddRounded, DeleteRounded, StarRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, limit, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import type { ProgramReviewerConfig } from '../../../lib/api/academic.api';

interface PersonOption {
  id: string;
  name: string;
  email: string;
}

interface ManageReviewCommitteeProps {
  reviewerConfig: ProgramReviewerConfig[];
  onChange: (reviewerConfig: ProgramReviewerConfig[]) => void;
}

export function ManageReviewCommittee({ reviewerConfig, onChange }: ManageReviewCommitteeProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch persons matching search query
  useEffect(() => {
    if (!currentTenantId || searchInput.trim().length < 2) {
      setPersonOptions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Fetch all people without a role filter so we catch persons whose
        // roles live in institutionalRoles instead of (or in addition to) types.
        const STUDENT_ONLY_TYPES = ['student', 'alumno'];
        const q = query(
          collection(db, `tenants/${currentTenantId}/people`),
          where('deletedAt', '==', null),
          limit(300),
        );
        const snap = await getDocs(q);
        const term = searchInput.toLowerCase();
        const results: PersonOption[] = snap.docs
          .filter((d) => {
            const data = d.data();
            // Exclude pure students (no institutional role at all)
            const types: string[] = data['types'] ?? [];
            const instRoles: string[] = data['institutionalRoles'] ?? [];
            const hasInstRole =
              types.some((t) => !STUDENT_ONLY_TYPES.includes(t)) || instRoles.length > 0;
            return hasInstRole;
          })
          .map((d) => ({
            id: d.id,
            name: `${d.data()['firstName'] ?? ''} ${d.data()['lastName'] ?? ''}`.trim(),
            email: d.data()['email'] ?? '',
          }))
          .filter(
            (p) => p.name.toLowerCase().includes(term) || p.email.toLowerCase().includes(term),
          )
          .filter((p) => !reviewerConfig.some((r) => r.personId === p.id))
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, 10);
        setPersonOptions(results);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, currentTenantId, reviewerConfig]);

  const handleAdd = () => {
    if (!selectedPerson) return;
    onChange([...reviewerConfig, { personId: selectedPerson.id, required: true }]);
    setSelectedPerson(null);
    setSearchInput('');
    setPersonOptions([]);
  };

  const handleRemove = (personId: string) => {
    onChange(reviewerConfig.filter((r) => r.personId !== personId));
  };

  const handleToggleRequired = (personId: string, required: boolean) => {
    onChange(reviewerConfig.map((r) => (r.personId === personId ? { ...r, required } : r)));
  };

  // Build display info for existing committee members from fetched options + fallback ids
  const [knownPersons, setKnownPersons] = useState<Record<string, PersonOption>>({});

  useEffect(() => {
    if (!currentTenantId || reviewerConfig.length === 0) return;
    const unknownIds = reviewerConfig.map((r) => r.personId).filter((id) => !knownPersons[id]);
    if (unknownIds.length === 0) return;

    // Fetch in parallel batches of up to 10 (Firestore 'in' limit)
    const batches = [];
    for (let i = 0; i < unknownIds.length; i += 10) {
      batches.push(unknownIds.slice(i, i + 10));
    }
    (async () => {
      const map: Record<string, PersonOption> = { ...knownPersons };
      for (const batch of batches) {
        const q = query(
          collection(db, `tenants/${currentTenantId}/people`),
          where('__name__', 'in', batch),
        );
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          map[d.id] = {
            id: d.id,
            name: `${d.data()['firstName'] ?? ''} ${d.data()['lastName'] ?? ''}`.trim(),
            email: d.data()['email'] ?? '',
          };
        });
      }
      setKnownPersons(map);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewerConfig, currentTenantId]);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            {t('reviewCommittee.heading')}
          </Typography>
          {reviewerConfig.length > 0 && (
            <Chip
              size="small"
              label={t('reviewCommittee.requiredCount', {
                count: reviewerConfig.filter((r) => r.required).length,
              })}
              sx={{
                bgcolor: 'rgba(99,102,241,0.12)',
                color: '#818cf8',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* ── Add Reviewer ── */}
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={2}>
        <Autocomplete
          fullWidth
          size="small"
          options={personOptions}
          value={selectedPerson}
          inputValue={searchInput}
          onInputChange={(_, v) => setSearchInput(v)}
          onChange={(_, v) => setSelectedPerson(v)}
          loading={loading}
          noOptionsText={
            searchInput.length < 2
              ? t('reviewCommittee.noOptionsMinChars')
              : t('reviewCommittee.noResults')
          }
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Avatar
                sx={{ width: 28, height: 28, mr: 1, fontSize: '0.75rem', bgcolor: 'primary.main' }}
              >
                {option.name.charAt(0)}
              </Avatar>
              <Stack>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => {
            const p = params as any;
            return (
              <TextField
                {...p}
                size="small"
                label={t('reviewCommittee.searchLabel')}
                placeholder={t('reviewCommittee.searchPlaceholder')}
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={16} />}
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }}
        />
        <Button
          variant="contained"
          disabled={!selectedPerson}
          onClick={handleAdd}
          startIcon={<AddRounded />}
          sx={{ minWidth: 110 }}
        >
          {t('reviewCommittee.add')}
        </Button>
      </Stack>

      {/* ── Committee List ── */}
      <Stack spacing={1.5}>
        {reviewerConfig.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.02)',
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('reviewCommittee.emptyState')}
            </Typography>
          </Paper>
        ) : (
          reviewerConfig.map((rev) => {
            const person = knownPersons[rev.personId];
            return (
              <Paper
                key={rev.personId}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderColor: rev.required ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.1)',
                }}
              >
                <Avatar
                  sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.85rem' }}
                >
                  {person ? person.name.charAt(0) : '?'}
                </Avatar>

                <Stack flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {person?.name ?? rev.personId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {person?.email ?? ''}
                  </Typography>
                </Stack>

                {rev.required && (
                  <Chip
                    size="small"
                    icon={<StarRounded sx={{ fontSize: '0.85rem !important' }} />}
                    label={t('reviewCommittee.requiredChip')}
                    sx={{
                      bgcolor: 'rgba(99,102,241,0.12)',
                      color: '#818cf8',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': { color: '#818cf8' },
                    }}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={rev.required}
                      onChange={(e) => handleToggleRequired(rev.personId, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {t('reviewCommittee.requiredCheckbox')}
                    </Typography>
                  }
                  sx={{ mr: 0, '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' } }}
                />

                <IconButton size="small" color="error" onClick={() => handleRemove(rev.personId)}>
                  <DeleteRounded fontSize="small" />
                </IconButton>
              </Paper>
            );
          })
        )}
      </Stack>

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
}
